import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { useStyles, withStyles } from 'legacyStyles';

import useInterval from 'utils/useInterval';

import SliderControls from './SliderControls';

const Content = styled.div`
  width: 100%;
`;

const styles = {
  root: {
    position: 'relative',
    width: '100%',
  },
  slide: {
    display: 'block',

    opacity: 0,
    transition: 'opacity 300ms ease-in',
    willChange: 'opacity',
  },
  fixedSlide: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  currentSlide: {
    opacity: 1,
    transition: 'opacity 300ms ease-out',
  },

  floatingControls: {
    position: 'absolute',
    bottom: 20,
    zIndex: 2,

    width: '100%',
  },
  controls: {
    width: '100%',
    padding: `${legacyTheme.spacing.s8} 0`,
  },
  floatingControlsWithBackground: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    zIndex: 2,
  },
};

const Slider = ({
  autoPlay,
  classes,
  children,
  controlsTheme,
  dataFrom,
  floatingControls,
  hideControls,
  onIndexChange,
  withBackgroundControls,
  parentIndex,
}) => {
  const size = children?.length || 0;
  const [index, setIndex] = useState(parentIndex);
  const [auto, setAuto] = useState(autoPlay);
  const { cx } = useStyles();

  useEffect(() => {
    setIndex(parentIndex);
  }, [parentIndex]);

  useEffect(() => {
    onIndexChange && onIndexChange(index);
  }, [onIndexChange, index]);

  const toggleAuto = () => autoPlay && setAuto(!auto);
  const increment = () => setIndex((index + 1) % size);

  useInterval(increment, auto ? 4000 : null);

  return (
    <div className={classes.root} onMouseEnter={toggleAuto} onMouseLeave={toggleAuto}>
      {children.map((child, i) => (
        <Content
          key={String(i)}
          className={cx(classes.slide, {
            [classes.fixedSlide]: i > 0,
            [classes.currentSlide]: i === index,
          })}
          data-click="change-slide"
          data-from={dataFrom}
          data-testid="slide"
          onClick={increment}
        >
          {child}
        </Content>
      ))}

      {size > 1 && !hideControls && (
        <SliderControls
          classes={{
            root: floatingControls
              ? withBackgroundControls
                ? classes.floatingControlsWithBackground
                : classes.floatingControls
              : classes.controls,
          }}
          currentIndex={index}
          dataFrom="block-consultation-slider"
          onClick={i => setIndex(i)}
          size={size}
          theme={controlsTheme}
          withBackground={withBackgroundControls}
        />
      )}
    </div>
  );
};

Slider.propTypes = {
  autoPlay: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  children: PropTypes.array.isRequired,
  controlsTheme: PropTypes.string,
  // The goal of this prop is to manage current slide from the parent
  parentIndex: PropTypes.number,
  dataFrom: PropTypes.string,
  floatingControls: PropTypes.bool,
  hideControls: PropTypes.bool,
  onIndexChange: PropTypes.func,
  withBackgroundControls: PropTypes.bool,
};

Slider.defaultProps = {
  parentIndex: 0,
  autoPlay: false,
  dataFrom: 'image-slider',
  floatingControls: false,
  withBackgroundControls: false,
};

export default withStyles(Slider, styles);
